import type { StrategyNode } from '../types/BaseNode';

// ============================================================
// Tree Layout Engine — Auto-spacing, flat coordinate map
// ============================================================
// Produces a Map<nodeId, NodeLayout> where each entry contains
// the absolute (x, y) to use for `position: absolute` placement.
// The algorithm is a standard Reingold-Tilford inspired two-pass:
//   Pass 1 (bottom-up):  calculate the total pixel WIDTH each subtree needs.
//   Pass 2 (top-down):   assign (x, y) coordinates.
// ============================================================

export interface NodeLayout {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    parentId: string | null;
    /** Which slot this node occupies in its parent */
    slot: 'left' | 'right' | 'child' | 'root' | 'entry' | 'exit' | 'main_market' | 'signal';
}

export type ActualDimensionsMap = Map<string, { width: number; height: number }>;

// ── Constants ────────────────────────────────────────────────
export const CARD_WIDTH = 280; // Default width
export const H_GAP = 40;  // horizontal gap between sibling subtrees
const EXPL_H_GAP = 70; // Adjusted gap for Variance node children
export const V_GAP = 60;  // vertical gap between parent and child rows
const HEADER_HEIGHT = 46;
const PLACEHOLDER_WIDTH = 200;
const PLACEHOLDER_HEIGHT = 80;

function estimateNodeWidth(node: StrategyNode, isCollapsed: boolean): number {
    if (isCollapsed) return 220;
    if (node.type === 'Placeholder') return 200;
    if (node.type === 'PnL') return 360;
    if (node.type === 'Variance') return 360;
    if (node.type === 'Composite') return 280;
    if (node.type === 'Formatter') return 240;
    if (node.type === 'Regression') return 360;
    if (node.type === 'Single') return 240;
    if (node.type === 'MeepMeep') return 360;
    return 240;
}

function estimateNodeHeight(node: StrategyNode, isCollapsed: boolean): number {
    if (isCollapsed) return HEADER_HEIGHT;
    if (node.type === 'Placeholder') return 80;
    if (node.type === 'PnL') return 240;
    if (node.type === 'Variance') return 260;
    if (node.type === 'Single') return 160;
    if (node.type === 'Formatter') return 180;
    if (node.type === 'Composite') return 140;
    if (node.type === 'MeepMeep') return 240;
    if (node.type === 'Regression') return 220;
    return 150;
}

// ── Pass 1: measure subtree extents ────────────────────────

interface Extents {
    leftExt: number;
    rightExt: number;
    height: number;
}

function getExtents(
    node: StrategyNode | undefined,
    isCollapsed: (id: string) => boolean,
    actualDims?: ActualDimensionsMap
): Extents {
    if (!node) return { leftExt: 0, rightExt: 0, height: 0 };

    const isColl = isCollapsed(node.id);
    const actual = actualDims?.get(node.id);

    // Priority: Actual measured size > Estimated size
    const nodeWidth = actual?.width ?? estimateNodeWidth(node, isColl);
    const nodeHeight = actual?.height ?? estimateNodeHeight(node, isColl);

    if (isColl) {
        return { leftExt: nodeWidth / 2, rightExt: nodeWidth / 2, height: nodeHeight };
    }

    if (node.type === 'Single') {
        return { leftExt: nodeWidth / 2, rightExt: nodeWidth / 2, height: nodeHeight };
    }

    if (node.type === 'Formatter') {
        const { child } = node.config as { child?: StrategyNode | null };
        const ext = getExtents(child ?? undefined, isCollapsed, actualDims);
        const effExt = child ? ext : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT };
        return {
            leftExt: Math.max(nodeWidth / 2, effExt.leftExt),
            rightExt: Math.max(nodeWidth / 2, effExt.rightExt),
            height: nodeHeight + (child || true ? V_GAP + effExt.height : 0) // child or placeholder
        };
    }

    if (node.type === 'Composite') {
        const { left, right } = node.config as { left?: StrategyNode; right?: StrategyNode };
        const L = getExtents(left ?? undefined, isCollapsed, actualDims);
        const R = getExtents(right ?? undefined, isCollapsed, actualDims);

        const effL = left ? L : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT };
        const effR = right ? R : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT };

        const dist = effL.rightExt + H_GAP + effR.leftExt;

        return {
            leftExt: Math.max(nodeWidth / 2, dist / 2 + effL.leftExt),
            rightExt: Math.max(nodeWidth / 2, dist / 2 + effR.rightExt),
            height: nodeHeight + V_GAP + Math.max(effL.height, effR.height)
        };
    }

    if (node.type === 'PnL') {
        const { entry, exit } = node.config as { entry?: StrategyNode; exit?: StrategyNode };
        const E = getExtents(entry ?? undefined, isCollapsed, actualDims);
        const X = getExtents(exit ?? undefined, isCollapsed, actualDims);

        const effE = entry ? E : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT };
        const effX = exit ? X : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT };

        const dist = effE.rightExt + H_GAP + effX.leftExt;

        return {
            leftExt: Math.max(nodeWidth / 2, dist / 2 + effE.leftExt),
            rightExt: Math.max(nodeWidth / 2, dist / 2 + effX.rightExt),
            height: nodeHeight + V_GAP + Math.max(effE.height, effX.height)
        };
    }

    if (node.type === 'Variance') {
        const children: (StrategyNode | undefined)[] = [
            node.config.target as StrategyNode | undefined,
            ...((node.config.parameters ?? []) as StrategyNode[]),
        ];
        // Up to 4 slots (1 target + 3 params) laid out as a row
        const extents = children.map(c => c
            ? getExtents(c, isCollapsed, actualDims)
            : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT }
        );
        // Ensure we always show at least target + 1 param placeholder = 2 slots
        const slots = Math.max(children.length, 2);
        const totalWidth = extents.reduce((w, e) => w + e.leftExt + e.rightExt, 0) + EXPL_H_GAP * (slots - 1);
        return {
            leftExt: Math.max(nodeWidth / 2, totalWidth / 2),
            rightExt: Math.max(nodeWidth / 2, totalWidth / 2),
            height: nodeHeight + V_GAP + Math.max(...extents.map(e => e.height))
        };
    }

    if (node.type === 'MeepMeep') {
        const { main_market, signal } = node.config as { main_market?: StrategyNode; signal?: StrategyNode };
        const M = getExtents(main_market ?? undefined, isCollapsed, actualDims);
        const S = getExtents(signal ?? undefined, isCollapsed, actualDims);

        const effM = main_market ? M : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT };
        const effS = signal ? S : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT };

        const dist = effM.rightExt + H_GAP + effS.leftExt;

        return {
            leftExt: Math.max(nodeWidth / 2, dist / 2 + effM.leftExt),
            rightExt: Math.max(nodeWidth / 2, dist / 2 + effS.rightExt),
            height: nodeHeight + V_GAP + Math.max(effM.height, effS.height)
        };
    }

    if (node.type === 'Regression') {
        const inputs = (node.config.inputs ?? []) as StrategyNode[];
        const extents = inputs.map(c =>
            c
                ? getExtents(c, isCollapsed, actualDims)
                : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT }
        );
        // Always show at least 1 empty placeholder slot
        const slots = Math.max(inputs.length + 1, 2);
        const placeholderExtent = { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT };
        const allExtents = [...extents, ...Array(slots - inputs.length).fill(placeholderExtent)];
        const totalWidth = allExtents.reduce((w, e) => w + e.leftExt + e.rightExt, 0) + EXPL_H_GAP * (allExtents.length - 1);
        return {
            leftExt: Math.max(nodeWidth / 2, totalWidth / 2),
            rightExt: Math.max(nodeWidth / 2, totalWidth / 2),
            height: nodeHeight + V_GAP + (extents.length > 0 ? Math.max(...extents.map(e => e.height)) : PLACEHOLDER_HEIGHT)
        };
    }

    return { leftExt: nodeWidth / 2, rightExt: nodeWidth / 2, height: nodeHeight };
}

// ── Pass 2: assign absolute (x, y) ──────────────────────────

function assignPositions(
    node: StrategyNode,
    cx: number,      // horizontal centre of this node's subtree
    y: number,       // vertical top of this generation
    parentId: string | null,
    slot: NodeLayout['slot'],
    map: Map<string, NodeLayout>,
    isCollapsed: (id: string) => boolean,
    actualDims?: ActualDimensionsMap
): void {
    const isColl = isCollapsed(node.id);
    const actual = actualDims?.get(node.id);

    const nodeWidth = actual?.width ?? estimateNodeWidth(node, isColl);
    const nodeHeight = actual?.height ?? estimateNodeHeight(node, isColl);
    const nodeX = cx - nodeWidth / 2;

    map.set(node.id, {
        id: node.id,
        x: nodeX,
        y,
        width: nodeWidth,
        height: nodeHeight,
        parentId,
        slot,
    });

    const childY = y + nodeHeight + V_GAP;

    if (isColl) {
        return; // Do not traverse into children of collapsed nodes
    }

    if (node.type === 'Composite') {
        const { left, right } = node.config as { left?: StrategyNode; right?: StrategyNode };
        const L = getExtents(left ?? undefined, isCollapsed, actualDims);
        const R = getExtents(right ?? undefined, isCollapsed, actualDims);

        const effL = left ? L : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT };
        const effR = right ? R : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT };

        const dist = effL.rightExt + H_GAP + effR.leftExt;

        if (left) {
            assignPositions(left, cx - dist / 2, childY, node.id, 'left', map, isCollapsed, actualDims);
        } else {
            const px = cx - dist / 2;
            map.set(`placeholder-${node.id}-left`, {
                id: `placeholder-${node.id}-left`,
                x: px - PLACEHOLDER_WIDTH / 2,
                y: childY,
                width: PLACEHOLDER_WIDTH,
                height: PLACEHOLDER_HEIGHT,
                parentId: node.id,
                slot: 'left',
            });
        }
        if (right) {
            assignPositions(right, cx + dist / 2, childY, node.id, 'right', map, isCollapsed, actualDims);
        } else {
            const px = cx + dist / 2;
            map.set(`placeholder-${node.id}-right`, {
                id: `placeholder-${node.id}-right`,
                x: px - PLACEHOLDER_WIDTH / 2,
                y: childY,
                width: PLACEHOLDER_WIDTH,
                height: PLACEHOLDER_HEIGHT,
                parentId: node.id,
                slot: 'right',
            });
        }
    }

    if (node.type === 'Formatter') {
        const { child } = node.config as { child?: StrategyNode | null };
        if (child) {
            assignPositions(child, cx, childY, node.id, 'child', map, isCollapsed, actualDims);
        } else {
            map.set(`placeholder-${node.id}-child`, {
                id: `placeholder-${node.id}-child`,
                x: cx - PLACEHOLDER_WIDTH / 2,
                y: childY,
                width: PLACEHOLDER_WIDTH,
                height: PLACEHOLDER_HEIGHT,
                parentId: node.id,
                slot: 'child',
            });
        }
    }

    if (node.type === 'PnL') {
        const { entry, exit } = node.config as { entry?: StrategyNode; exit?: StrategyNode };
        const E = getExtents(entry ?? undefined, isCollapsed, actualDims);
        const X = getExtents(exit ?? undefined, isCollapsed, actualDims);

        const effE = entry ? E : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT };
        const effX = exit ? X : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT };

        const dist = effE.rightExt + H_GAP + effX.leftExt;

        if (entry) {
            assignPositions(entry, cx - dist / 2, childY, node.id, 'entry', map, isCollapsed, actualDims);
        } else {
            const px = cx - dist / 2;
            map.set(`placeholder-${node.id}-entry`, {
                id: `placeholder-${node.id}-entry`,
                x: px - PLACEHOLDER_WIDTH / 2,
                y: childY,
                width: PLACEHOLDER_WIDTH,
                height: PLACEHOLDER_HEIGHT,
                parentId: node.id,
                slot: 'entry',
            });
        }
        if (exit) {
            assignPositions(exit, cx + dist / 2, childY, node.id, 'exit', map, isCollapsed, actualDims);
        } else {
            const px = cx + dist / 2;
            map.set(`placeholder-${node.id}-exit`, {
                id: `placeholder-${node.id}-exit`,
                x: px - PLACEHOLDER_WIDTH / 2,
                y: childY,
                width: PLACEHOLDER_WIDTH,
                height: PLACEHOLDER_HEIGHT,
                parentId: node.id,
                slot: 'exit',
            });
        }
    }

    if (node.type === 'Variance') {
        const target = node.config.target as StrategyNode | undefined;
        const parameters: StrategyNode[] = node.config.parameters ?? [];

        // 1. Gather all "effective" children (nodes or placeholders)
        const children: { node?: StrategyNode; placeholderId?: string; slot: NodeLayout['slot'] }[] = [];

        // Slot 0: Target
        children.push({ node: target, placeholderId: target ? undefined : `placeholder-${node.id}-target`, slot: 'entry' });

        // Slots 1..N: Parameters
        parameters.forEach(p => children.push({ node: p, slot: 'right' }));

        // Extra empty param slot if < 3
        if (parameters.length < 3) {
            children.push({ placeholderId: `placeholder-${node.id}-param-${parameters.length}`, slot: 'right' });
        }

        // 2. Measure extents for each child to determine its subtree width
        const childExtents = children.map(c => c.node
            ? getExtents(c.node, isCollapsed, actualDims)
            : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT }
        );

        // 3. Calculate total width and starting horizontal center
        const totalInnerWidth = childExtents.reduce((w, e) => w + e.leftExt + e.rightExt, 0) + EXPL_H_GAP * (children.length - 1);
        let currentX = cx - totalInnerWidth / 2;

        // 4. Assign positions
        children.forEach((child, i) => {
            const ext = childExtents[i];
            const subtreeCx = currentX + ext.leftExt;

            if (child.node) {
                assignPositions(child.node, subtreeCx, childY, node.id, child.slot, map, isCollapsed, actualDims);
            } else if (child.placeholderId) {
                map.set(child.placeholderId, {
                    id: child.placeholderId,
                    x: subtreeCx - PLACEHOLDER_WIDTH / 2,
                    y: childY,
                    width: PLACEHOLDER_WIDTH,
                    height: PLACEHOLDER_HEIGHT,
                    parentId: node.id,
                    slot: child.slot,
                });
            }

            currentX += ext.leftExt + ext.rightExt + EXPL_H_GAP;
        });
    }

    if (node.type === 'MeepMeep') {
        const { main_market, signal } = node.config as { main_market?: StrategyNode; signal?: StrategyNode };
        const M = getExtents(main_market ?? undefined, isCollapsed, actualDims);
        const S = getExtents(signal ?? undefined, isCollapsed, actualDims);

        const effM = main_market ? M : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT };
        const effS = signal ? S : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT };

        const dist = effM.rightExt + H_GAP + effS.leftExt;

        if (main_market) {
            assignPositions(main_market, cx - dist / 2, childY, node.id, 'main_market', map, isCollapsed, actualDims);
        } else {
            const px = cx - dist / 2;
            map.set(`placeholder-${node.id}-main_market`, {
                id: `placeholder-${node.id}-main_market`,
                x: px - PLACEHOLDER_WIDTH / 2,
                y: childY,
                width: PLACEHOLDER_WIDTH,
                height: PLACEHOLDER_HEIGHT,
                parentId: node.id,
                slot: 'main_market',
            });
        }
        if (signal) {
            assignPositions(signal, cx + dist / 2, childY, node.id, 'signal', map, isCollapsed, actualDims);
        } else {
            const px = cx + dist / 2;
            map.set(`placeholder-${node.id}-signal`, {
                id: `placeholder-${node.id}-signal`,
                x: px - PLACEHOLDER_WIDTH / 2,
                y: childY,
                width: PLACEHOLDER_WIDTH,
                height: PLACEHOLDER_HEIGHT,
                parentId: node.id,
                slot: 'signal',
            });
        }
    }

    if (node.type === 'Regression') {
        const inputs = (node.config.inputs ?? []) as StrategyNode[];
        const children: { node?: StrategyNode; placeholderId?: string; slotName: string }[] = [];

        // Existing inputs, each with an indexed slot name
        inputs.forEach((p, i) => children.push({ node: p, slotName: `input-${i}` }));

        // Always show at least 1 empty slot so the user can drop features
        children.push({ placeholderId: `placeholder-${node.id}-input-${inputs.length}`, slotName: `input-${inputs.length}` });

        const childExtents = children.map(c =>
            c.node
                ? getExtents(c.node, isCollapsed, actualDims)
                : { leftExt: PLACEHOLDER_WIDTH / 2, rightExt: PLACEHOLDER_WIDTH / 2, height: PLACEHOLDER_HEIGHT }
        );

        const totalInnerWidth = childExtents.reduce((w, e) => w + e.leftExt + e.rightExt, 0) + EXPL_H_GAP * (children.length - 1);
        let currentX = cx - totalInnerWidth / 2;

        children.forEach((child, i) => {
            const ext = childExtents[i];
            const subtreeCx = currentX + ext.leftExt;
            // Cast slotName as any since it's dynamically 'input-N' which extends the known slot union
            const slotVal = child.slotName as NodeLayout['slot'];

            if (child.node) {
                assignPositions(child.node, subtreeCx, childY, node.id, slotVal, map, isCollapsed, actualDims);
            } else if (child.placeholderId) {
                map.set(child.placeholderId, {
                    id: child.placeholderId,
                    x: subtreeCx - PLACEHOLDER_WIDTH / 2,
                    y: childY,
                    width: PLACEHOLDER_WIDTH,
                    height: PLACEHOLDER_HEIGHT,
                    parentId: node.id,
                    slot: slotVal,
                });
            }

            currentX += ext.leftExt + ext.rightExt + EXPL_H_GAP;
        });
    }
}

// ── Public API ────────────────────────────────────────────────

/** Calculate a flat coordinate map for all nodes in the tree. */
export function calculateLayout(
    root: StrategyNode,
    isCollapsed: (id: string) => boolean,
    actualDims?: ActualDimensionsMap
): Map<string, NodeLayout> {
    const map = new Map<string, NodeLayout>();
    const ext = getExtents(root, isCollapsed, actualDims);
    // Position root so that the absolute leftmost edge of its subtree touches x=0
    assignPositions(root, ext.leftExt, 0, null, 'root', map, isCollapsed, actualDims);
    return map;
}

/** Get the total bounding box (width × height) of the laid-out tree. */
export function calculateBounds(layoutMap: Map<string, NodeLayout>): { width: number; height: number } {
    let maxX = 0;
    let maxY = 0;
    for (const layout of layoutMap.values()) {
        maxX = Math.max(maxX, layout.x + layout.width);
        maxY = Math.max(maxY, layout.y + layout.height);
    }
    return { width: maxX, height: maxY };
}

/** Flatten a tree into an ordered array of nodes (breadth-first). */
export function flattenTree(root: StrategyNode, isCollapsed: (id: string) => boolean): StrategyNode[] {
    const result: StrategyNode[] = [];
    const queue: StrategyNode[] = [root];
    while (queue.length > 0) {
        const node = queue.shift()!;
        result.push(node);

        // Skip children if this node is collapsed
        if (isCollapsed(node.id)) {
            continue;
        }

        if (node.type === 'Composite') {
            const { left, right } = node.config as { left?: StrategyNode; right?: StrategyNode };
            if (left) {
                queue.push(left);
            } else {
                result.push({
                    id: `placeholder-${node.id}-left`,
                    type: 'Placeholder',
                    config: { parentId: node.id, slot: 'left' }
                } as any);
            }
            if (right) {
                queue.push(right);
            } else {
                result.push({
                    id: `placeholder-${node.id}-right`,
                    type: 'Placeholder',
                    config: { parentId: node.id, slot: 'right' }
                } as any);
            }
        }
        if (node.type === 'Formatter') {
            const { child } = node.config as { child?: StrategyNode | null };
            if (child) {
                queue.push(child);
            } else {
                result.push({
                    id: `placeholder-${node.id}-child`,
                    type: 'Placeholder',
                    config: { parentId: node.id, slot: 'child' }
                } as any);
            }
        }
        if (node.type === 'PnL') {
            const { entry, exit } = node.config as { entry?: StrategyNode; exit?: StrategyNode };
            if (entry) {
                queue.push(entry);
            } else {
                result.push({
                    id: `placeholder-${node.id}-entry`,
                    type: 'Placeholder',
                    config: { parentId: node.id, slot: 'entry' }
                } as any);
            }
            if (exit) {
                queue.push(exit);
            } else {
                result.push({
                    id: `placeholder-${node.id}-exit`,
                    type: 'Placeholder',
                    config: { parentId: node.id, slot: 'exit' }
                } as any);
            }
        }
        if (node.type === 'Variance') {
            const target = node.config.target as StrategyNode | undefined;
            const parameters: StrategyNode[] = node.config.parameters ?? [];

            if (target) {
                queue.push(target);
            } else {
                result.push({
                    id: `placeholder-${node.id}-target`,
                    type: 'Placeholder',
                    config: { parentId: node.id, slot: 'entry', label: 'target' }
                } as any);
            }
            parameters.forEach((p) => queue.push(p));
            if (parameters.length < 3) {
                result.push({
                    id: `placeholder-${node.id}-param-${parameters.length}`,
                    type: 'Placeholder',
                    config: { parentId: node.id, slot: 'right', label: 'parameter' }
                } as any);
            }
        }
        if (node.type === 'MeepMeep') {
            const { main_market, signal } = node.config as { main_market?: StrategyNode; signal?: StrategyNode };
            if (main_market) {
                queue.push(main_market);
            } else {
                result.push({
                    id: `placeholder-${node.id}-main_market`,
                    type: 'Placeholder',
                    config: { parentId: node.id, slot: 'main_market', label: 'Main Market (LOB)' }
                } as any);
            }
            if (signal) {
                queue.push(signal);
            } else {
                result.push({
                    id: `placeholder-${node.id}-signal`,
                    type: 'Placeholder',
                    config: { parentId: node.id, slot: 'signal', label: 'Alpha Signal' }
                } as any);
            }
        }
        if (node.type === 'Regression') {
            const inputs = (node.config.inputs ?? []) as StrategyNode[];
            // Enqueue all existing inputs for recursive rendering
            inputs.forEach(p => queue.push(p));
            // Always show at least 1 empty drop zone; slot must be 'input-N' so PlaceholderCard regex matches
            result.push({
                id: `placeholder-${node.id}-input-${inputs.length}`,
                type: 'Placeholder',
                config: { parentId: node.id, slot: `input-${inputs.length}`, label: 'feature input' }
            } as any);
        }
    }
    return result;
}
